import { useState } from 'react';
import { Sparkles, Repeat } from 'lucide-react';
import { memberPortal, useAsync } from '@fitboxx/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface PackageRow {
  id: string; name: string; description?: string; price: string;
  credits: number; valid_for_days: number; currency?: string;
}
interface PlanRow {
  id: string; name: string; description?: string; price: string;
  billing_period?: string; currency?: string;
}

export default function Shop() {
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">Shop</h1>
      <Tabs defaultValue="packs">
        <TabsList>
          <TabsTrigger value="packs"><Sparkles className="h-3.5 w-3.5 mr-1" />Packs</TabsTrigger>
          <TabsTrigger value="memberships"><Repeat className="h-3.5 w-3.5 mr-1" />Memberships</TabsTrigger>
        </TabsList>
        <TabsContent value="packs" className="mt-3"><PacksTab /></TabsContent>
        <TabsContent value="memberships" className="mt-3"><PlansTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function money(p: string, cur = 'MUR') {
  const n = Number(p ?? 0);
  if (!Number.isFinite(n)) return p;
  return `${cur} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function PacksTab() {
  const { data, loading } = useAsync(
    () => memberPortal.packages() as Promise<{ results?: PackageRow[] } | PackageRow[]>,
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const rows: PackageRow[] = Array.isArray(data) ? data : (data?.results ?? []);

  async function buy(p: PackageRow) {
    setBusyId(p.id);
    try {
      const r = await memberPortal.checkout({
        items: [{ kind: 'package', ref_id: p.id, quantity: 1 }],
        provider: 'mips',
        return_url: location.origin + '/wallet',
        cancel_url: location.origin + '/shop?cancelled=1',
      }) as { redirect_url?: string };
      if (r.redirect_url) location.href = r.redirect_url;
      else window.alert('Checkout started but no redirect URL was returned.');
    } catch (e) { window.alert(`Checkout failed: ${(e as Error).message}`); }
    finally { setBusyId(null); }
  }

  if (loading) return <Skeleton className="h-32 w-full" />;
  if (rows.length === 0) {
    return <Card className="p-6 text-center text-sm text-muted-foreground">No class packs available right now.</Card>;
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {rows.map((p) => (
        <Card key={p.id} className="p-4 flex flex-col">
          <div className="font-semibold">{p.name}</div>
          {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
          <div className="text-xs text-muted-foreground mt-2">
            {p.credits} credits · valid {p.valid_for_days} days
          </div>
          <div className="text-2xl font-semibold tabular-nums mt-3">{money(p.price, p.currency)}</div>
          <Button className="mt-3" onClick={() => void buy(p)} disabled={busyId === p.id}>
            {busyId === p.id ? 'Starting…' : 'Buy'}
          </Button>
        </Card>
      ))}
    </div>
  );
}

function PlansTab() {
  const { data, loading } = useAsync(
    () => memberPortal.membershipPlans() as Promise<{ results?: PlanRow[] } | PlanRow[]>,
  );
  const rows: PlanRow[] = Array.isArray(data) ? data : (data?.results ?? []);

  if (loading) return <Skeleton className="h-32 w-full" />;
  if (rows.length === 0) {
    return <Card className="p-6 text-center text-sm text-muted-foreground">No memberships available right now.</Card>;
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {rows.map((p) => (
        <Card key={p.id} className="p-4 flex flex-col">
          <div className="font-semibold">{p.name}</div>
          {p.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{p.description}</div>}
          <div className="text-xs text-muted-foreground mt-2 capitalize">
            {p.billing_period || 'monthly'}
          </div>
          <div className="text-2xl font-semibold tabular-nums mt-3">{money(p.price, p.currency)}</div>
          <Button className="mt-3" variant="outline"
                  onClick={() => window.alert('Memberships are activated by the studio. We will get in touch shortly.')}>
            Request to join
          </Button>
        </Card>
      ))}
    </div>
  );
}
